/**
 * @file Descriptions 描述列表
 * @description 键值对展示（详情页字段）。
 *
 * @whenToUse 详情只读信息、订单摘要。
 * @whenNotToUse 可编辑表单（用 FormField）。
 */

import type { ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'

export type DescriptionItem = {
  /** 唯一键 */
  key: string
  /** 标签 */
  label: ReactNode
  /** 内容 */
  children: ReactNode
  /** 跨列数（按 columns 计） */
  span?: number
}

export type DescriptionsProps = {
  items: DescriptionItem[]
  /** 列数 */
  columns?: 1 | 2 | 3
  /** 标题 */
  title?: ReactNode
  className?: string
}

export function Descriptions({
  items,
  columns = 2,
  title,
  className,
}: DescriptionsProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {title ? (
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      ) : null}
      <dl
        className={cn(
          'grid gap-x-6 gap-y-4 rounded-xl border border-border bg-raised p-4',
          columns === 1 && 'grid-cols-1',
          columns === 2 && 'grid-cols-1 sm:grid-cols-2',
          columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {items.map((item) => (
          <div
            key={item.key}
            className="min-w-0"
            style={
              item.span && item.span > 1
                ? { gridColumn: `span ${Math.min(item.span, columns)}` }
                : undefined
            }
          >
            <dt className="text-xs text-muted">{item.label}</dt>
            <dd className="mt-1 text-sm text-foreground">{item.children}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
